/**
 * Activity timeline — "who did what, when", grouped by day (newest first).
 *
 * Used by the Activity page (all activity, with filters) and inside a
 * shipment's history dialog (pass `entityId` to show just that shipment).
 */
import { useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { getActivityFeed } from "network/service";

const ACTION_STYLE = {
  create: { color: "#2e7d32", icon: "＋", label: "Created" },
  update: { color: "#1565c0", icon: "✎", label: "Updated" },
  delete: { color: "#c62828", icon: "✕", label: "Deleted" },
  status: { color: "#ef6c00", icon: "⇄", label: "Status" },
  tracking: { color: "#6a1b9a", icon: "◉", label: "Tracking" },
  vendor: { color: "#00838f", icon: "⛟", label: "Vendor" },
  document: { color: "#5d4037", icon: "▤", label: "Document" },
  login: { color: "#546e7a", icon: "⏻", label: "Sign-in" },
  test: { color: "#00838f", icon: "⚡", label: "Test" },
  sync: { color: "#00838f", icon: "⟳", label: "Sync" },
};

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const dayKey = (d) => new Date(d).toDateString();
const dayHeading = (d) => {
  const date = new Date(d);
  const today = new Date();
  const yest = new Date(Date.now() - 864e5);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yest.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

/** Human-friendly view of the request body, skipping noise. */
function DetailList({ details }) {
  if (!details || typeof details !== "object") return null;
  const entries = Object.entries(details).filter(
    ([k, v]) => v !== "" && v !== null && v !== undefined && !["_id", "id"].includes(k)
  );
  if (!entries.length) return null;
  const show = (v) => (typeof v === "object" ? JSON.stringify(v).slice(0, 300) : String(v));
  return (
    <MDBox mt={0.75} p={1} sx={{ background: "#f6f8fb", borderRadius: 1.5, maxHeight: 220, overflow: "auto" }}>
      {entries.map(([k, v]) => (
        <MDTypography key={k} variant="caption" display="block" sx={{ wordBreak: "break-word" }}>
          <b>{k.replace(/_/g, " ")}:</b> {show(v)}
        </MDTypography>
      ))}
    </MDBox>
  );
}

export default function ActivityTimeline({ filters = {}, entityId, pageSize = 50, compact = false }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const fetchPage = (p, append) => {
    setLoading(true);
    getActivityFeed({ ...filters, ...(entityId ? { entity_id: entityId } : {}), page: p, limit: pageSize })
      .then((r) => {
        setRows((prev) => (append ? [...prev, ...(r.data.rows || [])] : r.data.rows || []));
        setTotal(r.data.total || 0);
        setPage(p);
      })
      .catch(() => {
        if (!append) setRows([]);
      })
      .finally(() => setLoading(false));
  };

  // Refetch from page 1 whenever the filters change.
  const filterKey = JSON.stringify({ filters, entityId });
  useEffect(() => {
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Group consecutive rows (already newest-first) by day.
  const groups = [];
  rows.forEach((r) => {
    const k = dayKey(r.created_at);
    const last = groups[groups.length - 1];
    if (last && last.key === k) last.items.push(r);
    else groups.push({ key: k, heading: dayHeading(r.created_at), items: [r] });
  });

  if (!loading && rows.length === 0) {
    return (
      <MDTypography variant="button" color="text">
        No activity recorded{entityId ? " for this shipment yet" : " for these filters"}.
      </MDTypography>
    );
  }

  return (
    <MDBox>
      {groups.map((g) => (
        <MDBox key={g.key} mb={2}>
          <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1}>
            {g.heading}
          </MDTypography>
          <MDBox sx={{ position: "relative", pl: 4.5 }}>
            <MDBox sx={{ position: "absolute", left: 15, top: 4, bottom: 4, width: "2px", background: "#e3e6ea" }} />
            {g.items.map((r) => {
              const st = ACTION_STYLE[r.action] || { color: "#607d8b", icon: "•", label: r.action };
              const isOpen = !!open[r._id];
              return (
                <MDBox key={r._id} mb={1.25} sx={{ position: "relative" }}>
                  <MDBox
                    title={r.user_name}
                    sx={{
                      position: "absolute", left: -36, top: 2, width: 30, height: 30, borderRadius: "50%",
                      background: st.color, color: "#fff", fontSize: 11, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 0 0 3px #fff",
                    }}
                  >
                    {initials(r.user_name)}
                  </MDBox>
                  <MDBox
                    onClick={() => setOpen((o) => ({ ...o, [r._id]: !o[r._id] }))}
                    sx={{
                      border: "1px solid #e8ebef", borderRadius: 2, p: compact ? 1 : 1.25, background: "#fff",
                      cursor: "pointer", "&:hover": { borderColor: "#c5d4ea" },
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <MDTypography variant="button" fontWeight="bold">{r.user_name || "Unknown user"}</MDTypography>
                      {r.user_role ? <MDTypography variant="caption" color="text">{r.user_role}</MDTypography> : null}
                      <Chip
                        size="small"
                        label={`${st.icon} ${st.label}`}
                        sx={{ height: 18, fontSize: "0.65rem", color: "#fff", background: st.color }}
                      />
                      {!entityId && r.entity_label ? (
                        <Chip size="small" variant="outlined" label={r.entity_label} sx={{ height: 18, fontSize: "0.65rem" }} />
                      ) : null}
                      <MDTypography variant="caption" color="text" sx={{ ml: "auto" }}>
                        {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="button" fontWeight="regular" display="block" mt={0.25}>
                      {r.summary}
                    </MDTypography>
                    <Collapse in={isOpen} unmountOnExit>
                      <DetailList details={r.details} />
                      <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                        {r.method} {r.path}{r.ip ? ` · ${r.ip}` : ""} · {new Date(r.created_at).toLocaleString()}
                      </MDTypography>
                    </Collapse>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        </MDBox>
      ))}
      <MDBox display="flex" alignItems="center" gap={2}>
        {rows.length < total && (
          <MDButton variant="outlined" color="info" size="small" disabled={loading} onClick={() => fetchPage(page + 1, true)}>
            Load more ({total - rows.length} older)
          </MDButton>
        )}
        {loading && <CircularProgress size={18} />}
      </MDBox>
    </MDBox>
  );
}
